import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button, Alert, AlertDescription } from '@aaas/shared';
import { WidgetForm } from '../components/WidgetForm';
import { useCreateWidgetMutation } from '../hooks';
import type { WidgetFormData } from '../types/widget.types';

const CreateWidgetPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const createMutation = useCreateWidgetMutation();

  const handleSubmit = async (data: WidgetFormData) => {
    try {
      setError('');
      const result = await createMutation.mutateAsync(data);
      // Navigate to the new widget's detail page
      navigate(`/kms/widgets/${result.id}`);
    } catch (err) {
      console.error('Failed to create widget:', err);
      setError(err instanceof Error ? err.message : 'Failed to create widget. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate('/kms/widgets');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/kms/widgets')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-3xl font-bold">Create New Widget</h1>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <WidgetForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel="Create Widget"
        isSubmitting={createMutation.isPending}
      />
    </div>
  );
};

export default CreateWidgetPage;
